import React, { useState } from 'react';
import { Stack, styled } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import { ContainerLayout } from 'components/ContainerLayout/ContainerLayout';
import { BackButton } from 'components/BackButton/BackButton';
import { CareProgramDetailPage } from 'components/CareProgramDetailPage/CareProgramDetailPage';
import { SearchBar } from 'components/SearchBar/SearchBar';
import { CareProgramSearchResults } from 'components/Resources/CareProgramSearchResults';
import { useDebouncedValue } from 'hooks/useDebouncedvalue';
import { useCareProgramRequest } from 'features/resources/hooks';
import { usePendo } from 'features/pendo/hooks/usePendo';
import { PendoEventType } from 'features/pendo/types';
import type { Program } from 'features/resources/types';

const SEARCH_DEBOUNCE_MS = 300;

const PageColumn = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
}));

const SearchHeaderRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 1.75),
  backgroundColor: theme.palette.background.paper,
}));

const ScrollArea = styled('div')(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  backgroundColor: theme.palette.brandBackgroundPrimary.main,
  paddingBottom: theme.spacing(15),
  [theme.breakpoints.up('md')]: {
    paddingLeft: theme.spacing(7),
    paddingRight: theme.spacing(7),
  },
}));

export const CareProgramSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const { trackEvent } = usePendo();
  const { debouncedValue, flush } = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
  const {
    selectedProgram,
    programDetail,
    isDetailModalOpen,
    enrollmentStatus,
    handleProgramClick,
    handleModalClose,
  } = useCareProgramRequest();

  const handleSearchSubmit = () => flush();
  const handleResultClick = (program: Program) => {
    trackEvent(PendoEventType.SPECIFIC_CARE_PROGRAM, {
      eventType: PendoEventType.SPECIFIC_CARE_PROGRAM,
      programId: String(program.id),
    });
    handleProgramClick(program);
  };

  const handleBackButtonClick = () => navigate('/resources/topics');

  return (
    <ContainerLayout>
      <PageColumn>
        <SearchHeaderRow>
          <BackButton onClick={handleBackButtonClick} />
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onSubmit={handleSearchSubmit}
            placeholder="Search programs"
            autoFocus
          />
        </SearchHeaderRow>
        <ScrollArea>
          <CareProgramSearchResults
            searchTerm={debouncedValue}
            onProgramClick={handleResultClick}
            selectedProgramId={selectedProgram?.id}
          />
        </ScrollArea>
      </PageColumn>

      {programDetail && (
        <CareProgramDetailPage
          program={programDetail}
          open={isDetailModalOpen}
          onClose={handleModalClose}
          enrollmentRequested={enrollmentStatus?.hasRequestPending || enrollmentStatus?.isEnrolled}
        />
      )}
    </ContainerLayout>
  );
};
